export function validateRtpCapabilities(caps) {
    if (typeof caps !== "object")
        throw new TypeError("caps is not an object");
    // codecs is optional. If unset, fill with an empty array.
    if (caps.codecs && !Array.isArray(caps.codecs))
        throw new TypeError("caps.codecs is not an array");
    else if (!caps.codecs)
        caps.codecs = [];
    for (const codec of caps.codecs) {
        validateRtpCodecCapability(codec);
    }
    // headerExtensions is optional. If unset, fill with an empty array.
    if (caps.headerExtensions && !Array.isArray(caps.headerExtensions))
        throw new TypeError("caps.headerExtensions is not an array");
    else if (!caps.headerExtensions)
        caps.headerExtensions = [];
    for (const ext of caps.headerExtensions) {
        validateRtpHeaderExtension(ext);
    }
}
export function validateRtpCodecCapability(codec) {
    const MimeTypeRegex = new RegExp("^(audio|video)/(.+)", "i");
    if (typeof codec !== "object")
        throw new TypeError("codec is not an object");
    // mimeType is mandatory.
    if (!codec.mimeType || typeof codec.mimeType !== "string")
        throw new TypeError("missing codec.mimeType");
    const mimeTypeMatch = MimeTypeRegex.exec(codec.mimeType);
    if (!mimeTypeMatch)
        throw new TypeError("invalid codec.mimeType");
    // Just override kind with media component of mimeType.
    codec.kind = mimeTypeMatch[1].toLowerCase();
    // preferredPayloadType is optional.
    if (codec.preferredPayloadType && typeof codec.preferredPayloadType !== "number")
        throw new TypeError("invalid codec.preferredPayloadType");
    // clockRate is mandatory.
    if (typeof codec.clockRate !== "number")
        throw new TypeError("missing codec.clockRate");
    // channels is optional. If unset, set it to 1 (just if audio).
    if (codec.kind === "audio") {
        if (typeof codec.channels !== "number")
            codec.channels = 1;
    }
    else {
        delete codec.channels;
    }
    // parameters is optional. If unset, set it to an empty object.
    if (!codec.parameters || typeof codec.parameters !== "object")
        codec.parameters = {};
    for (const key of Object.keys(codec.parameters)) {
        let value = codec.parameters[key];
        if (value === undefined) {
            codec.parameters[key] = "";
            value = "";
        }
        if (typeof value !== "string" && typeof value !== "number") {
            throw new TypeError(`invalid codec parameter [key:${key}s, value:${value}]`);
        }
        // Specific parameters validation.
        if (key === "apt") {
            if (typeof value !== "number")
                throw new TypeError("invalid codec apt parameter");
        }
    }
    // rtcpFeedback is optional. If unset, set it to an empty array.
    if (!codec.rtcpFeedback || !Array.isArray(codec.rtcpFeedback))
        codec.rtcpFeedback = [];
    for (const fb of codec.rtcpFeedback) {
        validateRtcpFeedback(fb);
    }
}
export function validateRtcpFeedback(fb) {
    if (typeof fb !== "object")
        throw new TypeError("fb is not an object");
    // type is mandatory.
    if (!fb.type || typeof fb.type !== "string")
        throw new TypeError("missing fb.type");
    // parameter is optional. If unset set it to an empty string.
    if (!fb.parameter || typeof fb.parameter !== "string")
        fb.parameter = "";
}
export function validateRtpHeaderExtension(ext) {
    if (typeof ext !== "object")
        throw new TypeError("ext is not an object");
    // kind is mandatory.
    if (ext.kind !== "audio" && ext.kind !== "video")
        throw new TypeError("invalid ext.kind");
    // uri is mandatory.
    if (!ext.uri || typeof ext.uri !== "string")
        throw new TypeError("missing ext.uri");
    // preferredId is mandatory.
    /* if (typeof ext.preferredId !== 'number') {
          throw new TypeError('missing ext.preferredId');
      } */
    // preferredEncrypt is optional. If unset set it to false.
    if (ext.preferredEncrypt && typeof ext.preferredEncrypt !== "boolean")
        throw new TypeError("invalid ext.preferredEncrypt");
    else if (!ext.preferredEncrypt)
        ext.preferredEncrypt = false;
    // direction is optional. If unset set it to sendrecv.
    if (ext.direction && typeof ext.direction !== "string")
        throw new TypeError("invalid ext.direction");
    else if (!ext.direction)
        ext.direction = "sendrecv";
}
export function validateSctpCapabilities(caps) {
    if (typeof caps !== "object")
        throw new TypeError("caps is not an object");
    // numStreams is mandatory.
    if (!caps.numStreams || typeof caps.numStreams !== "object")
        throw new TypeError("missing caps.numStreams");
    validateNumSctpStreams(caps.numStreams);
}
export function validateNumSctpStreams(numStreams) {
    if (typeof numStreams !== "object")
        throw new TypeError("numStreams is not an object");
    // OS is mandatory.
    if (typeof numStreams.OS !== "number")
        throw new TypeError("missing numStreams.OS");
    // MIS is mandatory.
    if (typeof numStreams.MIS !== "number")
        throw new TypeError("missing numStreams.MIS");
}
export function getRecvRtpCapabilities(extendedRtpCapabilities) {
    const rtpCapabilities = {
        codecs: [],
        headerExtensions: []
    };
    for (const extendedCodec of extendedRtpCapabilities.codecs) {
        const codec = {
            mimeType: extendedCodec.mimeType,
            kind: extendedCodec.kind,
            preferredPayloadType: extendedCodec.remotePayloadType,
            clockRate: extendedCodec.clockRate,
            channels: extendedCodec.channels,
            parameters: extendedCodec.localParameters,
            rtcpFeedback: extendedCodec.rtcpFeedback
        };
        rtpCapabilities.codecs.push(codec);
        // Add RTX codec.
        if (!extendedCodec.remoteRtxPayloadType)
            continue;
        const rtxCodec = {
            mimeType: `${extendedCodec.kind}/rtx`,
            kind: extendedCodec.kind,
            preferredPayloadType: extendedCodec.remoteRtxPayloadType,
            clockRate: extendedCodec.clockRate,
            parameters: {
                apt: extendedCodec.remotePayloadType
            },
            rtcpFeedback: []
        };
        rtpCapabilities.codecs.push(rtxCodec);
        // TODO: In the future, we need to add FEC, CN, etc, codecs.
    }
    for (const extendedExtension of extendedRtpCapabilities.headerExtensions) {
        // Ignore RTP extensions not valid for receiving.
        if (extendedExtension.direction !== "sendrecv" && extendedExtension.direction !== "recvonly") {
            continue;
        }
        const ext = {
            kind: extendedExtension.kind,
            uri: extendedExtension.uri,
            preferredId: extendedExtension.recvId,
            preferredEncrypt: extendedExtension.encrypt,
            direction: extendedExtension.direction
        };
        rtpCapabilities.headerExtensions.push(ext);
    }
    return rtpCapabilities;
}
export function canSend(kind, extendedRtpCapabilities) {
    return extendedRtpCapabilities.codecs.some((codec) => codec.kind === kind);
}
export function getSendingRtpParameters(kind, extendedRtpCapabilities) {
    const rtpParameters = {
        mid: undefined,
        codecs: [],
        headerExtensions: [],
        encodings: [],
        rtcp: {}
    };
    for (const extendedCodec of extendedRtpCapabilities.codecs) {
        if (extendedCodec.kind !== kind)
            continue;
        const codec = {
            mimeType: extendedCodec.mimeType,
            payloadType: extendedCodec.localPayloadType,
            clockRate: extendedCodec.clockRate,
            channels: extendedCodec.channels,
            parameters: extendedCodec.localParameters,
            rtcpFeedback: extendedCodec.rtcpFeedback
        };
        rtpParameters.codecs.push(codec);
        // Add RTX codec.
        if (extendedCodec.localRtxPayloadType) {
            const rtxCodec = {
                mimeType: `${extendedCodec.kind}/rtx`,
                payloadType: extendedCodec.localRtxPayloadType,
                clockRate: extendedCodec.clockRate,
                parameters: {
                    apt: extendedCodec.localPayloadType
                },
                rtcpFeedback: []
            };
            rtpParameters.codecs.push(rtxCodec);
        }
    }
    for (const extendedExtension of extendedRtpCapabilities.headerExtensions) {
        // Ignore RTP extensions of a different kind and those not valid for sending.
        if ((extendedExtension.kind && extendedExtension.kind !== kind) ||
            (extendedExtension.direction !== "sendrecv" && extendedExtension.direction !== "sendonly")) {
            continue;
        }
        const ext = {
            uri: extendedExtension.uri,
            id: extendedExtension.sendId,
            encrypt: extendedExtension.encrypt,
            parameters: {}
        };
        rtpParameters.headerExtensions.push(ext);
    }
    return rtpParameters;
}
